import React from 'react';
import { Mail, MapPin, Phone, Clock, Navigation } from 'lucide-react';
import Reveal from '../components/Reveal';
import { images, contactInfo, stores } from '../mock';

const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative h-[46vh] min-h-[340px] flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center slide-zoom" style={{ backgroundImage: `url(${image})` }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c1f]/90 via-[#0f2c1f]/70 to-[#0f2c1f]/90" />
    <div className="relative z-10 text-center px-5 pt-16 max-w-3xl">
      <p className="animate-fade-up text-[#dcbf74] text-xs md:text-sm font-semibold tracking-[0.35em] mb-4">{eyebrow}</p>
      <h1 className="animate-fade-up font-serif-display text-white text-4xl md:text-6xl font-bold text-shadow-lg" style={{ animationDelay: '0.15s' }}>{title}</h1>
      {subtitle && <p className="animate-fade-up text-white/80 mt-4" style={{ animationDelay: '0.25s' }}>{subtitle}</p>}
    </div>
  </section>
);

const Contact = () => {
  return (
    <div>
      <PageHero
        eyebrow="GET IN TOUCH"
        title="Contact Us"
        subtitle="Visit us in-store or reach out by phone or email — our team would love to help with any enquiry."
        image={images.market}
      />

      <section className="py-20 md:py-28 bg-[#faf7f0]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* General info */}
          <Reveal className="lg:col-span-1">
            <div className="bg-[#163a29] rounded-3xl p-8 md:p-10 h-full text-white">
              <h2 className="font-serif-display text-2xl md:text-3xl font-bold mb-5">Reach Us</h2>
              <p className="text-white/70 leading-relaxed mb-8">
                We'd love to hear from you. Call your nearest store or send us an email and our team will get back to you promptly.
              </p>
              <div className="flex gap-4 mb-6">
                <div className="w-11 h-11 rounded-full bg-[#c8a24c]/15 flex items-center justify-center shrink-0">
                  <Mail className="text-[#c8a24c]" size={20} />
                </div>
                <div>
                  <p className="text-white/50 text-xs tracking-widest uppercase mb-1">Email</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-white font-medium hover:text-[#c8a24c] transition-colors break-all">{contactInfo.email}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[#c8a24c]/15 flex items-center justify-center shrink-0">
                  <Clock className="text-[#c8a24c]" size={20} />
                </div>
                <div>
                  <p className="text-white/50 text-xs tracking-widest uppercase mb-1">Opening Hours</p>
                  <p className="text-white/90">Mon–Sun: 8:00 AM – 9:00 PM</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Store contact cards - each with its own phone number */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {stores.map((s, i) => (
              <Reveal key={s.id} delay={i * 100}>
                <div className="bg-white rounded-3xl p-7 md:p-8 h-full shadow-sm border border-[#e8e0cf] flex flex-col">
                  <span className="inline-block w-fit px-3 py-1 rounded-full bg-[#c8a24c]/15 text-[#163a29] text-[10px] font-semibold tracking-widest mb-4">{s.tag}</span>
                  <h3 className="font-serif-display text-[#163a29] text-2xl font-bold mb-5">{s.name}</h3>
                  <ul className="space-y-3.5 mb-6 flex-1">
                    <li className="flex gap-3 text-[#3a4a42]">
                      <MapPin size={18} className="text-[#c8a24c] shrink-0 mt-0.5" />
                      <span>{s.address}</span>
                    </li>
                    <li className="flex gap-3 text-[#3a4a42] items-center">
                      <Phone size={18} className="text-[#c8a24c] shrink-0" />
                      <a href={`tel:${s.phone.replace(/[^0-9+]/g, '')}`} className="font-semibold text-[#163a29] hover:text-[#c8a24c] transition-colors">
                        {s.phone}
                      </a>
                    </li>
                    <li className="flex gap-3 text-[#3a4a42] items-center">
                      <Clock size={18} className="text-[#c8a24c] shrink-0" />
                      <span>{s.hours}</span>
                    </li>
                  </ul>
                  <a
                    href={s.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#163a29] text-white font-semibold hover:bg-[#0f2c1f] transition-all duration-300"
                  >
                    <Navigation size={17} />
                    Get Directions
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
